import { DB } from '@services/database.service';
import { useUIStore } from '@stores/v4/useUIStore';
import { useAthleteStore } from '@stores/v4/useAthleteStore';
import { useHistoryStore } from '@stores/v4/useHistoryStore';
import { 
    hydratePerformanceFromTemplate, 
    preparePerformanceLogsForInsert,
    calculatePerformanceMetrics,
    reconstructSessionFromLogs,
} from '@shared/logic/performance-logic';
import { AIWorkoutService } from '@ai-services/ai-workout.service';
import { ActiveSession } from '@/types/app.types';

/**
 * SESSION ORCHESTRATOR
 * Handles complex async I/O for starting and finishing training sessions.
 * HARDENED: Temporal safety logic to prevent negative durations.
 */
export const SessionOrchestrator = {
    /**
     * Prepares and starts a fresh or existing session.
     */
    async startSession(
        dayId: string | null, 
        customDate: string | null = null, 
        sourceEventId: string | null = null, 
        isManual: boolean = false, 
        customEndDate: string | null = null, 
        label: string | undefined = undefined
    ): Promise<{ session: ActiveSession, retroDate: string | null }> {
        const history = useHistoryStore.getState();
        const sourceEvent = history.events.find((e: any) => e.id === sourceEventId);
        let session: ActiveSession;

        // SMART START TIME: 
        // 1. Use customDate if provided (Manual/Retro)
        // 2. Use existing start_time if editing (ACTIVE/COMPLETED)
        // 3. Otherwise, use ACTUAL 'Now' time to prevent snapped-time errors
        let targetStartTime = customDate || new Date().toISOString();
        if (sourceEventId && !customDate) {
            const isActiveOrDone = sourceEvent?.status === 'ACTIVE' || sourceEvent?.status === 'COMPLETED';
            if (isActiveOrDone) {
                targetStartTime = sourceEvent.start_time;
            } else {
                // If starting a PLANNED event today, use actual 'now' instead of the snapped planned time
                const isToday = new Date(sourceEvent?.start_time).toDateString() === new Date().toDateString();
                if (isToday) targetStartTime = new Date().toISOString();
                else targetStartTime = sourceEvent?.start_time;
            }
        }

        const isToday = new Date(targetStartTime).toDateString() === new Date().toDateString();
        const retroDate = isToday ? (sourceEvent?.status === 'COMPLETED' ? targetStartTime : null) : targetStartTime;

        if (sourceEventId && sourceEvent?.status !== 'ACTIVE' && sourceEvent?.status !== 'COMPLETED') {
            history.updateOptimisticEvent(sourceEventId, { status: 'ACTIVE', start_time: targetStartTime });
            await DB.updateUnifiedEventStatus(sourceEventId, 'ACTIVE');
            // Sync start time to DB if we adjusted it from planned
            await DB.updateUnifiedEvent(sourceEventId, { start_time: targetStartTime });
        }

        if (dayId) {
            const { data: template } = await DB.fetchSessionTemplate(dayId);
            if (!template) throw new Error("Template not found");
            session = hydratePerformanceFromTemplate(template, dayId, targetStartTime, sourceEventId, isManual);
            session.label = label || (sourceEvent?.label as string) || (template as any).label || (template as any).name || 'Performance';
        } else {
            session = {
                id: crypto.randomUUID(),
                startTime: targetStartTime,
                logs: {},
                blocks: [{
                    id: 'adhoc-block',
                    label: 'AD-HOC',
                    block_type: 'LINEAR',
                    items: [],
                    sort_order: 0
                }],
                p_consume_event_id: sourceEventId || undefined,
                label: label || (sourceEvent?.label as string) || 'Manual Performance'
            };
        }

        session.p_consume_event_id = sourceEventId || undefined;
        session.intended_end_time = customEndDate || sourceEvent?.end_time;

        const targetSessionId = sourceEvent?.completed_session_id;
        if (targetSessionId) {
            const { data: existingLogs } = await DB.fetchSessionDetails(targetSessionId, useUIStore.getState().getFKConstraint());
            if (existingLogs && existingLogs.length > 0) {
                session = reconstructSessionFromLogs(session, existingLogs);
                session.id = targetSessionId; 
                session.isEditMode = true; 
                session.endTime = sourceEvent.end_time || undefined; 
            }
        }

        return { session, retroDate };
    },

    /**
     * Finalizes and syncs a completed session to the database.
     * HARDENED: Temporal clamping ensures end_time >= start_time + 1m.
     */
    async finishSession(session: ActiveSession, retroactiveDate: string | null): Promise<void> {
        const history = useHistoryStore.getState();
        const { data: userData, error: userErr } = await DB.getUser();
        if (userErr) throw userErr;
        const user = userData.user;
        
        const isRetro = !!retroactiveDate || session.isManualMode || session.isEditMode;
        const startTime = session.startTime;
        
        let end_time = new Date().toISOString();
        if (isRetro) {
            if (session.endTime) end_time = session.endTime;
            else if (session.intended_end_time) end_time = session.intended_end_time;
            else {
                const sourceEvent = history.events.find((e: any) => e.id === session.p_consume_event_id);
                end_time = sourceEvent?.end_time || new Date(new Date(startTime).getTime() + 60000).toISOString();
            }
        }

        // --- TEMPORAL CLAMPING ---
        // If end_time is before or equal to start_time, force it to be 1 minute after start_time
        const startMs = new Date(startTime).getTime();
        const endMs = new Date(end_time).getTime();
        if (endMs <= startMs) {
            console.warn("[SessionOrchestrator] Negative duration detected. Clamping end_time.");
            end_time = new Date(startMs + 60000).toISOString();
        }

        const sessionPayload: any = {
            user_id: user?.id,
            start_time: startTime,
            end_time: end_time
        };

        if (session.program_day_id) sessionPayload.program_day_id = session.program_day_id;

        let sessionId = session.id;
        
        // 1. UPSERT SESSION
        if (session.isEditMode) {
            await DB.updateCompletedSession(sessionId, sessionPayload);
            // If it exists, clear the old logs before inserting the new ones
            await DB.clearPerformanceLogs(sessionId);
        } else {
            const { data: sessionData, error: sErr } = await DB.insertCompletedSession(sessionPayload);
            if (sErr) throw sErr;
            sessionId = sessionData.id;
        }

        // 2. INSERT LOGS
        const logsToInsert = preparePerformanceLogsForInsert(sessionId, session.logs, session.blocks, session.startTime);
        if (logsToInsert.length > 0) {
            const { error: lErr } = await DB.insertPerformanceLogs(logsToInsert);
            if (lErr) throw lErr;
        }
        
        // 3. REGISTER EVENT & OPTIMISTIC UPDATE
        const { data: eventData } = await DB.registerPerformanceEvent(sessionId, session.p_consume_event_id || null);
        let eventId = (eventData as any)?.id || session.p_consume_event_id;
        
        if (!eventId) {
            const { data: events } = await DB.fetchEvents(user?.id || '');
            const found = events?.find((e: any) => e.completed_session_id === sessionId);
            eventId = found?.id;
        }

        const allLogsList = Object.values(session.logs).flat();
        const metrics = calculatePerformanceMetrics(allLogsList);

        if (eventId) {
            // OPTIMISTIC COMPLETED STATE
            history.updateOptimisticEvent(eventId, { 
                status: 'COMPLETED', 
                end_time: end_time,
                preview_data: metrics as any 
            });

            // Update DB
            await DB.updateUnifiedEvent(eventId, { 
                preview_data: metrics as any,
                status: 'COMPLETED',
                end_time: end_time
            });

            // 4. BACKGROUND AI
            this.triggerBackgroundAI(eventId, sessionId, end_time, startTime, metrics);
        }

        await history.fetchGlobalHistory(true);
    },

    async triggerBackgroundAI(eventId: string, sessionId: string, end_time: string, startTime: string, metrics: any) {
        try {
            const { profile } = useAthleteStore.getState();
            const durationMinutes = Math.round((new Date(end_time).getTime() - new Date(startTime).getTime()) / 60000);
            const { data: details } = await DB.fetchSessionDetails(sessionId, useUIStore.getState().getFKConstraint());
            const { data: burnRes } = await AIWorkoutService.estimateSessionBurn(details || [], profile, durationMinutes);
            
            if (burnRes?.kcal_burn) {
                const updatedPreview = { ...metrics, estimated_burn: burnRes.kcal_burn, burn_logic: burnRes.logic_summary };
                await DB.updateUnifiedEvent(eventId, { preview_data: updatedPreview });
                useHistoryStore.getState().updateOptimisticEvent(eventId, { preview_data: updatedPreview });
            }
        } catch (aiErr) {
            console.error("[SessionOrchestrator] Background AI FAILED:", aiErr);
        }
    }
};
